import { apiClient, apiEndpoint } from './api.services';
import { API } from '../constants/api.constants';

export interface SelectOptionDto {
  label: string;
  value: string;
}
export interface GradingGroupOptionDto extends SelectOptionDto {
  /** Backend forwards the grading group's sizeRanges so the per-line Size cell can scope its options. */
  sizeRanges?: Array<string | { name: string; isSampleSize?: boolean }>;
}
export interface ProductDetailsBatchDto {
  gradingGroups: GradingGroupOptionDto[];
  colourLibraries: string[];
  /** Per-product leg-length master entries (DevelopmentOrders.legLengths populated with name). */
  legLengths: SelectOptionDto[];
}
export interface CoreProductDetailsDto {
  /** Per-product colourway options (DevelopmentOrders.colourLibraries -> ColourWays, resolved to name). */
  colourways: SelectOptionDto[];
  /** Per-product size options (DevelopmentOrders.sizeRanges). */
  sizes: SelectOptionDto[];
  /** Per-product leg-length options (DevelopmentOrders.legLengths populated with name). */
  lengths: SelectOptionDto[];
}
export interface BomDocumentDto {
  _id: string;
  fileName: string;
  createdAt: string | null;
  docType: string | null;
}
export interface MeasurementDocumentDto {
  _id: string;
  documentTitle: string;
}
export interface PoDocumentDto {
  _id: string;
  fileName: string;
}
export interface DesignDocumentDto {
  _id: string;
  documentName: string;
}
export interface SampleDto {
  _id: string;
  sampleNumber: string;
  createdAt: string | null;
  supplier: string;
  /** Resolved sample-type name (from MainSamples/OtherSamples master); '' when unresolved. */
  sampleTypeName: string;
}
export interface ProductSampleDto {
  _id: string;
  sampleType: string;
  sampleTypeObj: { _id: string } | null;
  supplier: string;
  parentId: string | null;
  revisionRootId: string | null;
  versionNumber: number;
  /** Group the sample belongs to — drives Sample Type Group eligibility (see sampleChain). */
  sampleTypeGroupId: string | null;
  typeOfSampleType: string | null;
  isNewSample: boolean;
}
export interface SampleTypeGroupDto {
  _id: string;
  sampleGroupName: string;
  seqNumber: number | null;
  licensed: string | null;
  isDefault: boolean;
  sampleTypeAutomation: Array<{
    sampleType: { _id: string; name: string } | null;
    configuration: unknown[];
  }>;
}

export interface BatchEnvelope<T> {
  code: number;
  message: string;
  data: Record<string, T>;
}

const MAX_BATCH_IDS = 50;

/** Splits ids into ≤50-id chunks (server cap), fires them in parallel, merges keyed results. */
async function postBatch<T>(url: string, devOrderIds: string[], extra?: Record<string, unknown>): Promise<Record<string, T>> {
  const chunks: string[][] = [];
  for (let i = 0; i < devOrderIds.length; i += MAX_BATCH_IDS) {
    chunks.push(devOrderIds.slice(i, i + MAX_BATCH_IDS));
  }
  const responses = (await Promise.all(
    chunks.map((chunk) => apiClient.post(`${apiEndpoint}${url}`, { devOrderIds: chunk, ...extra })),
  )) as unknown as Array<BatchEnvelope<T> | undefined>;
  const merged: Record<string, T> = {};
  for (const res of responses) {
    if (res?.code === 200 && res.data) Object.assign(merged, res.data);
  }
  return merged;
}

export const getProductDetailsBatch = (devOrderIds: string[]) =>
  postBatch<ProductDetailsBatchDto>(API.MULTI_SUBMIT_BATCH.PRODUCT_DETAILS, devOrderIds);

export const getCoreProductDetailsBatch = (devOrderIds: string[]) =>
  postBatch<CoreProductDetailsDto>(API.MULTI_SUBMIT_BATCH.CORE_PRODUCT_DETAILS, devOrderIds);

export const getBomDocumentsBatch = (devOrderIds: string[]) =>
  postBatch<BomDocumentDto[]>(API.MULTI_SUBMIT_BATCH.BOM_DOCUMENTS, devOrderIds);

export const getMeasurementDocumentsBatch = (devOrderIds: string[]) =>
  postBatch<MeasurementDocumentDto[]>(API.MULTI_SUBMIT_BATCH.MEASUREMENT_DOCUMENTS, devOrderIds);

export const getPoDocumentsBatch = (devOrderIds: string[]) =>
  postBatch<PoDocumentDto[]>(API.MULTI_SUBMIT_BATCH.PO_DOCUMENTS, devOrderIds);

export const getDesignDocumentsBatch = (devOrderIds: string[]) =>
  postBatch<DesignDocumentDto[]>(API.MULTI_SUBMIT_BATCH.DESIGN_DOCUMENTS, devOrderIds);

export const getSamplesBatch = (devOrderIds: string[], supplierId: string) =>
  postBatch<SampleDto[]>(API.MULTI_SUBMIT_BATCH.SAMPLES, devOrderIds, { supplierId });

export const getProductSamplesBatch = (devOrderIds: string[]) =>
  postBatch<ProductSampleDto[]>(API.MULTI_SUBMIT_BATCH.PRODUCT_SAMPLES, devOrderIds);

export const getSampleTypeGroupsBatch = (devOrderIds: string[]) =>
  postBatch<SampleTypeGroupDto[]>(API.MULTI_SUBMIT_BATCH.SAMPLE_TYPE_GROUPS, devOrderIds);
